<template>
  <div class="half-life-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$N(t) = N₀ · (1/2)^(t/T)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>初始原子数 N₀</label>
            <span class="val">100</span>
          </div>
          <div class="hud-item orange">
            <label>残余原子数 N(t)</label>
            <span class="val">{{ remainingCount }}</span>
          </div>
          <div class="hud-item blue">
             <label>已过天数 t</label>
             <span class="val">{{ currentTime }} <small>Days</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 450 260" preserveAspectRatio="xMidYMid meet">
          <!-- 统计图背景 (指数轴) -->
          <g transform="translate(280, 40)">
             <path d="M 0,0 L 0,160 L 150,160" fill="none" stroke="#334155" stroke-dasharray="2,2" />
             <path :d="decayGraphPath" fill="none" stroke="#475569" stroke-width="1.5" stroke-dasharray="4,2" />
             <circle :cx="timeToX(currentTime)" :cy="countToY(remainingCount)" r="4" fill="#ef4444" filter="url(#glow-h)" />
             <text x="75" y="175" text-anchor="middle" fill="#64748b" font-size="9">指数衰减曲线</text>
          </g>

          <!-- 原子阵列区 (10x10 Grid) -->
          <g transform="translate(30, 40)">
             <g v-for="(a, i) in atoms" :key="'a'+i" :transform="`translate(${(i%10)*22}, ${Math.floor(i/10)*22})`">
                <circle r="8" :fill="a.isDecayed ? '#334155' : '#10b981'" :opacity="a.isDecayed ? 0.3 : 1" stroke="#064e3b" />
                <circle v-if="!a.isDecayed" r="2" fill="#fff" opacity="0.6" />
             </g>
             <text x="0" y="235" fill="#94a3b8" font-size="10">同位素样本阵列 (100 Atoms)</text>
          </g>
          
          <defs>
             <filter id="glow-h"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <div class="physics-caption">
           原理：无论样本有多少，每过一个半衰期 T，总会有一半的原子发生衰变
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节半衰期时长 T (决定衰减速度)</label>
                 <el-slider v-model="halfLifeT" :min="10" :max="100" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">推移时间轴 (t 天)：{{ currentTime }}</label>
                 <el-slider v-model="currentTime" :min="0" :max="300" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="resetAtoms">重新投样</el-button>
              </div>
           </div>
        </div>

        <!-- 针对性文字对齐：深紫高对比度卡片，杜绝字迹模糊 -->
        <div class="discovery-card-discovery-special" v-latex>
           <div class="icon-wrap"><i class="el-icon-timer"></i></div>
           <div class="text-content">
             <p><strong>实战物理/核化学：</strong>单个原子的衰变是随机的，但宏观遵循严格的统计律。<strong>观察规律：</strong>
                1. 调节半衰期滑块：半衰期越长，右上角曲线越平缓；
                2. 观察概率：将 $t$ 调到两个 $T$ 时，样本中只会剩下约 $1/4$。
                这解释了**碳-14 断代法**判定古董年代的底层数学依据。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'IsotopeHalfLifeViz',
  data() {
    return {
      halfLifeT: 40,
      currentTime: 0,
      atoms: []
    }
  },
  computed: {
    remainingCount() {
       return this.atoms.filter(a => !a.isDecayed).length;
    },
    decayGraphPath() {
       let p = "M 0,0 ";
       for(let t=0; t<=150; t+=10) {
          const ratio = Math.pow(0.5, t / (this.halfLifeT * 0.5)); // Scaling for plot
          p += `L ${t},${160 - ratio * 160} `;
       }
       return p;
    }
  },
  watch: {
    currentTime(newTime) {
       this.syncDecay(newTime);
    },
    halfLifeT() { this.syncDecay(this.currentTime); }
  },
  mounted() { this.initAtoms(); },
  methods: {
    initAtoms() {
       this.atoms = Array.from({length: 100}, () => ({
          decayThreshold: Math.random(), // Probability threshold for this atom
          isDecayed: false
       }));
    },
    syncDecay(t) {
       // Probability of decaying by time t: P = 1 - (1/2)^(t/T)
       const pDecay = 1 - Math.pow(0.5, t / this.halfLifeT);
       this.atoms.forEach(a => {
          a.isDecayed = a.decayThreshold < pDecay;
       });
    },
    resetAtoms() { this.initAtoms(); this.currentTime = 0; },
    timeToX(t) { return (t/300) * 150; },
    countToY(count) { return 160 - (count/100) * 160; }
  }
}
</script>

<style scoped lang="scss">
.half-life-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf4ff; color: #701a75; border: 1px solid #f5d0fe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } &.blue { border-left: 3px solid #3b82f6; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }

/* 针对性加固：深紫高对比度卡片，解决文字看不清 */
.discovery-card-discovery-special { 
  background: #fdf4ff; 
  padding: 12px 16px; 
  border-radius: 10px; 
  border: 1px solid #f5d0fe; 
  display: flex; gap: 14px; 
  align-items: center;
  i { color: #86198f; font-size: 1.4rem; }
  p { 
    margin: 0; 
    font-size: 11.5px; 
    color: #4a044e; // 深紫色文字，高对比度
    line-height: 1.6;
    strong { color: #701a75; font-weight: 900; }
  }
}
</style>
